import { Alert, AlertTitle, Button, Stack, Typography } from "@mui/material";
import { Link } from "react-router";
import type { AssistantTask } from "../../../../api/types/assistantTypes";
import RunPreview from "./RunPreview";
import { formatTaskTime, getAttentionTitle } from "./taskStatus";

interface Props {
  task: AssistantTask;
  preview?: string;
  chatPath?: string;
  actionsDisabled: boolean;
  onFix: () => void;
  onRunNow: () => void;
  onResume: () => void;
  onDismiss: () => void;
  onDelete: () => void;
}

const TaskAttentionBox = ({
  task,
  preview,
  chatPath,
  actionsDisabled,
  onFix,
  onRunNow,
  onResume,
  onDismiss,
  onDelete,
}: Props) => {
  const reason = task.attentionReason;
  if (!reason) {
    return null;
  }

  const isPaused = reason === "PausedFailures" || reason === "PausedNoAccess";
  const canFix = isPaused || reason === "LastRunFailed";
  const failurePreview =
    (reason === "LastRunFailed" || reason === "PausedFailures") && task.lastRunStatus === "Failed"
      ? preview
      : undefined;
  const openChatButton = chatPath && (
    <Button variant="outlined" component={Link} to={chatPath}>
      Open the run&apos;s chat
    </Button>
  );

  return (
    <Alert severity={reason === "Skipped" ? "warning" : "error"}>
      <AlertTitle>{getAttentionTitle(task)}</AlertTitle>
      <Stack spacing={1.5}>
        {isPaused && <Typography>It won&apos;t run again until you resume it.</Typography>}
        {reason === "Skipped" && task.nextRunAt && (
          <Typography>The next run is {formatTaskTime(task.nextRunAt)}.</Typography>
        )}
        {reason === "PausedFailures" && failurePreview && <Typography>The last failure:</Typography>}
        {failurePreview && <RunPreview text={failurePreview} />}
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {canFix && (
            <Button variant="contained" disabled={actionsDisabled} onClick={onFix}>
              Fix with the Assistant
            </Button>
          )}
          {isPaused ? (
            <>
              <Button variant="outlined" disabled={actionsDisabled} onClick={onResume}>
                Resume
              </Button>
              {openChatButton}
              <Button variant="outlined" color="error" disabled={actionsDisabled} onClick={onDelete}>
                Delete task
              </Button>
            </>
          ) : (
            <>
              <Button
                variant={canFix ? "outlined" : "contained"}
                disabled={actionsDisabled || task.isRunning}
                onClick={onRunNow}
              >
                {reason === "Skipped" ? "Run now" : "Run again now"}
              </Button>
              {reason === "LastRunFailed" && openChatButton}
              <Button disabled={actionsDisabled} onClick={onDismiss}>
                Dismiss
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Alert>
  );
};

export default TaskAttentionBox;
